// src/components/ColoredText.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Renders a tagged color string as styled <span> elements.
//
// Usage on your PUBLIC WEBSITE:
//
//   import { ColoredText } from "./components/ColoredText";
//
//   // Title — large, on dark background
//   <ColoredText
//     tagged={blog.title}
//     defaultColor="#FFFFFF"
//     style={{ fontSize: 32, fontWeight: 800 }}
//   />
//
//   // Subheading
//   <ColoredText
//     tagged={blog.subheading}
//     defaultColor="#FFD700"
//     style={{ fontSize: 18 }}
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { parseColoredText } from "../utils/parseColoredText";

/**
 * ColoredText component
 *
 * Props:
 *   tagged        {string}  - The tagged string e.g. "[yellow]word[/yellow] rest"
 *   defaultColor  {string}  - Fallback color for untagged plain text (default: inherit)
 *   style         {object}  - Extra CSS applied to the outer <span>
 *   className     {string}  - Optional CSS class on the outer <span>
 *   as            {string}  - HTML tag to render as: "h1", "h2", "p", "span" (default: "span")
 */
export function ColoredText({
  tagged = "",
  defaultColor = "inherit",
  style = {},
  className = "",
  as: Tag = "span",
}) {
  const segments = parseColoredText(tagged);

  // If there are no segments (empty string), render nothing
  if (!segments.length) return null;

  // If there are no color tags at all, just render plain text
  const hasAnyColor = segments.some(s => s.color !== null);
  if (!hasAnyColor) {
    return (
      <Tag className={className} style={{ color: defaultColor, ...style }}>
        {tagged}
      </Tag>
    );
  }

  return (
    <Tag className={className} style={style}>
      {segments.map((seg, i) => (
        <span
          key={i}
          style={{ color: seg.color || defaultColor }}
        >
          {seg.text}
        </span>
      ))}
    </Tag>
  );
}

/**
 * BlogTitle — convenience wrapper for blog titles on dark backgrounds
 * Defaults: yellow for [yellow] tags, white for untagged text
 */
export function BlogTitle({ tagged, style = {} }) {
  return (
    <ColoredText
      tagged={tagged}
      defaultColor="#FFFFFF"
      style={{ fontWeight: 800, lineHeight: 1.3, ...style }}
    />
  );
}

/**
 * BlogSubheading — convenience wrapper for subheadings
 * Defaults: white for [white] tags, light-grey for untagged
 */
export function BlogSubheading({ tagged, style = {} }) {
  return (
    <ColoredText
      tagged={tagged}
      defaultColor="#E0E0E0"
      style={{ fontWeight: 500, lineHeight: 1.5, ...style }}
    />
  );
}

export default ColoredText;
